import React, { useState, useEffect, useCallback } from "react";
import {
  DollarSign,
  Clock,
  CheckCircle,
  User,
  Calendar,
  Package,
  AlertCircle,
  Building,
} from "lucide-react";
import { format } from "date-fns";
import { DataTable, DataTablePagination } from "@/components/ui/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import {
  adminRewardsService,
  AffiliateReward,
  BrandReward,
  AgencyReward,
  RewardSearchParams,
  RewardSearchResponse,
  RewardStatus,
  RewardType,
  UpdateRewardStatusDto,
  RewardStats,
} from "../../services/admin/rewards.admin.service";

// Custom debounce hook for search
function useDebounce(callback: () => void, delay: number) {
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  return React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(callback, delay);
  }, [callback, delay]);
}

export default function UnifiedRewards() {
  const [rewards, setRewards] = useState<
    (AffiliateReward | BrandReward | AgencyReward)[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<RewardType>(
    RewardType.AFFILIATE
  );
  const [stats, setStats] = useState<RewardStats | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Filters
  const [statusFilter, setStatusFilter] = useState<RewardStatus | "">("");
  const [searchQuery, setSearchQuery] = useState("");

  // Load rewards data
  const loadRewards = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: RewardSearchParams = {
        page: currentPage,
        limit: pageSize,
        type: selectedType,
      };

      if (statusFilter) {
        params.status = statusFilter as RewardStatus;
      }

      const response: RewardSearchResponse =
        await adminRewardsService.getRewards(params);
      setRewards(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.total);

      // Load stats
      const statsData = await adminRewardsService.getRewardStats(selectedType);
      setStats(statsData);
    } catch (err) {
      setError("Failed to load rewards data");
      console.error("Error loading rewards:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, selectedType]);

  // Debounced search
  const debouncedSearch = useDebounce(loadRewards, 500);

  // Load data on mount and when dependencies change
  useEffect(() => {
    debouncedSearch();
  }, [loadRewards]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, selectedType]);

  // Handle status update
  const handleStatusUpdate = async (
    rewardId: string,
    newStatus: RewardStatus
  ) => {
    try {
      const updateDto: UpdateRewardStatusDto = { rewardStatus: newStatus };

      if (selectedType === RewardType.AFFILIATE) {
        await adminRewardsService.updateAffiliateRewardStatus(
          rewardId,
          updateDto
        );
      } else if (selectedType === RewardType.BRAND) {
        await adminRewardsService.updateBrandRewardStatus(rewardId, updateDto);
      } else if (selectedType === RewardType.AGENCY) {
        await adminRewardsService.updateAgencyRewardStatus(rewardId, updateDto);
      }

      // Reload data
      await loadRewards();
    } catch (err) {
      setError("Failed to update reward status");
      console.error("Error updating status:", err);
    }
  };


  // Define table columns based on reward type
  const getColumns = (): ColumnDef<
    AffiliateReward | BrandReward | AgencyReward
  >[] => {
    const baseColumns: ColumnDef<
      AffiliateReward | BrandReward | AgencyReward
    >[] = [
      {
        id: "reward",
        header: "Reward",
        meta: { width: "w-52" },
        cell: ({ row: { original: reward } }) => (
          <div className="flex items-center space-x-2 min-w-0">
            <Package className="h-4 w-4 text-amber-500 shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-foreground truncate">
                {reward.reward}
              </div>
              <div className="text-sm text-muted-foreground truncate">
                {reward.rewardName}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        meta: { width: "w-44" },
        cell: ({ row: { original: reward } }) => {
          const statusConfig = {
            [RewardStatus.PENDING]: {
              icon: Clock,
              color: "text-yellow-600 dark:text-yellow-400",
              bg: "bg-yellow-50 dark:bg-yellow-500/10",
              border: "border-yellow-200 dark:border-yellow-500/30",
            },
            [RewardStatus.INCOMPLETE_ADDRESS]: {
              icon: AlertCircle,
              color: "text-red-600 dark:text-red-400",
              bg: "bg-red-50 dark:bg-red-500/10",
              border: "border-red-200 dark:border-red-500/30",
            },
            [RewardStatus.DELIVERED]: {
              icon: CheckCircle,
              color: "text-green-600 dark:text-green-400",
              bg: "bg-green-50 dark:bg-green-500/10",
              border: "border-green-200 dark:border-green-500/30",
            },
          };

          const config = statusConfig[reward.rewardStatus];
          const Icon = config.icon;

          return (
            <div className="flex items-center space-x-2">
              <div
                className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config.color} ${config.bg} ${config.border} border`}
              >
                <Icon className="h-3 w-3" />
                <span>{reward.rewardStatus.replace("_", " ")}</span>
              </div>
            </div>
          );
        },
      },
      {
        id: "earnedAt",
        header: "Earned At",
        meta: { width: "w-36" },
        cell: ({ row: { original: reward } }) => (
          <div className="flex items-center space-x-1 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{format(new Date(reward.earnedAt), "MMM dd, yyyy")}</span>
          </div>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        meta: { width: "w-56" },
        cell: ({ row: { original: reward } }) => (
          <div className="flex items-center space-x-2">
            {reward.rewardStatus !== RewardStatus.DELIVERED && (
              <button
                onClick={() =>
                  handleStatusUpdate(reward.id, RewardStatus.DELIVERED)
                }
                className="px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 rounded hover:bg-green-100 dark:hover:bg-green-500/20 transition-colors"
              >
                Mark Delivered
              </button>
            )}
            {reward.rewardStatus === RewardStatus.PENDING && (
              <button
                onClick={() =>
                  handleStatusUpdate(reward.id, RewardStatus.INCOMPLETE_ADDRESS)
                }
                className="px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors"
              >
                Mark Incomplete
              </button>
            )}
          </div>
        ),
      },
    ];

    if (selectedType === RewardType.AFFILIATE) {
      return [
        {
          id: "affiliate",
          header: "Affiliate",
          meta: { width: "w-44" },
          cell: ({ row: { original: reward } }) => (
            <div className="flex items-center space-x-2 min-w-0">
              <User className="h-4 w-4 text-amber-500 shrink-0" />
              <div className="font-medium text-foreground truncate">
                {(reward as AffiliateReward).affiliateName}
              </div>
            </div>
          ),
        },
        {
          id: "email",
          header: "Email",
          meta: { width: "w-52" },
          cell: ({ row: { original: reward } }) => (
            <div className="text-sm text-muted-foreground truncate">
              {(reward as AffiliateReward).affiliateEmail}
            </div>
          ),
        },
        ...baseColumns,
        {
          id: "earningAmount",
          header: "Earning Amount",
          meta: { width: "w-36" },
          cell: ({ row: { original: reward } }) => (
            <div className="flex items-center space-x-1 font-medium text-foreground">
              <DollarSign className="h-4 w-4 text-green-500" />
              <span>
                ${(reward as AffiliateReward).earningAmount.toFixed(2)}
              </span>
            </div>
          ),
        },
      ];
    } else if (selectedType === RewardType.BRAND) {
      return [
        {
          id: "brand",
          header: "Brand",
          meta: { width: "w-44" },
          cell: ({ row: { original: reward } }) => (
            <div className="flex items-center space-x-2 min-w-0">
              <Building className="h-4 w-4 text-[#d7785b] shrink-0" />
              <div className="font-medium text-foreground truncate">
                {(reward as BrandReward).brandName}
              </div>
            </div>
          ),
        },
        {
          id: "email",
          header: "Email",
          meta: { width: "w-52" },
          cell: ({ row: { original: reward } }) => (
            <div className="text-sm text-muted-foreground truncate">
              {(reward as BrandReward).brandEmail}
            </div>
          ),
        },
        ...baseColumns,
        {
          id: "revenueAmount",
          header: "Revenue Amount",
          meta: { width: "w-36" },
          cell: ({ row: { original: reward } }) => (
            <div className="flex items-center space-x-1 font-medium text-foreground">
              <DollarSign className="h-4 w-4 text-green-500" />
              <span>${(reward as BrandReward)?.revenueAmount?.toFixed(2)}</span>
            </div>
          ),
        },
      ];
    } else {
      // Agency rewards
      return [
        {
          id: "agency",
          header: "Agency",
          meta: { width: "w-44" },
          cell: ({ row: { original: reward } }) => (
            <div className="flex items-center space-x-2 min-w-0">
              <Building className="h-4 w-4 text-blue-500 shrink-0" />
              <div className="font-medium text-foreground truncate">
                {(reward as AgencyReward).agencyName}
              </div>
            </div>
          ),
        },
        {
          id: "email",
          header: "Email",
          meta: { width: "w-52" },
          cell: ({ row: { original: reward } }) => (
            <div className="text-sm text-muted-foreground truncate">
              {(reward as AgencyReward).agencyEmail}
            </div>
          ),
        },
        ...baseColumns,
        {
          id: "revenueAmount",
          header: "Revenue Amount",
          meta: { width: "w-36" },
          cell: ({ row: { original: reward } }) => (
            <div className="flex items-center space-x-1 font-medium text-foreground">
              <DollarSign className="h-4 w-4 text-green-500" />
              <span>
                ${(reward as AgencyReward)?.revenueAmount?.toFixed(2)}
              </span>
            </div>
          ),
        },
      ];
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Rewards Management
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage affiliate, brand, and agency rewards, track statuses, and
            process payouts
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <div className="bg-card overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Package className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-muted-foreground truncate">
                      Total Rewards
                    </dt>
                    <dd className="text-lg font-medium text-foreground">
                      {stats.totalRewards}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-muted-foreground truncate">
                      Pending
                    </dt>
                    <dd className="text-lg font-medium text-foreground">
                      {stats.pendingRewards}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-muted-foreground truncate">
                      Delivered
                    </dt>
                    <dd className="text-lg font-medium text-foreground">
                      {stats.deliveredRewards}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Status Filter */}
            <div>
              <label
                htmlFor="status"
                className="block text-sm font-medium text-muted-foreground"
              >
                Status
              </label>
              <select
                id="status"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as RewardStatus | "")
                }
                className="mt-1 block border p-3 w-full  pr-10  text-base border-border focus:outline-none focus:ring-black focus:border-black focus:ring-1 sm:text-sm rounded-md"
              >
                <option value="">All Statuses</option>
                <option value={RewardStatus.PENDING}>Pending</option>
                <option value={RewardStatus.INCOMPLETE_ADDRESS}>
                  Incomplete Address
                </option>
                <option value={RewardStatus.DELIVERED}>Delivered</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label
                htmlFor="search"
                className="block text-sm font-medium text-muted-foreground"
              >
                Search
              </label>
              <input
                type="text"
                id="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${selectedType}s...`}
                className="mt-1 p-3 border block w-full border-border rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm">
        <UnderlineTabs<RewardType>
          className="px-5 pt-2"
          accent="terracotta"
          tabs={[
            { value: RewardType.AFFILIATE, label: "Affiliate" },
            { value: RewardType.BRAND, label: "Brand" },
            { value: RewardType.AGENCY, label: "Agency" },
          ]}
          value={selectedType}
          onValueChange={setSelectedType}
        />
        <DataTable
          data={rewards}
          columns={getColumns()}
          isLoading={loading}
          emptyText={`No ${selectedType} rewards found`}
        />

        {/* Pagination */}
        {totalItems > pageSize && (
          <DataTablePagination
            page={currentPage}
            totalPages={totalPages}
            total={totalItems}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            onPageChange={setCurrentPage}
            noun="rewards"
          />
        )}
      </div>
    </div>
  );
}
